import React, { useState, useEffect, useMemo } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, Loader,Menu} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../utils/auth';
import { getPontosChronos,getDadosFolha, chronosLogin } from '../../api';



// ======================= COMPONENTE MULTISELECT =======================
const MultiSelect = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left bg-white border rounded-lg text-sm hover:border-gray-400"
      >
        {value.length > 0 ? `${value.length} selecionado(s)` : placeholder}
      </button>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.map(opt => (
            <label key={opt.value} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={value.includes(opt.value)}
                onChange={() => toggleOption(opt.value)}
                className="mr-2"
              />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ======================= COMPONENTE ICONE =======================
const IconeOrdenacao = ({ campo, ordenacao, handleOrdenar }) => {
  return (
    <span
      onClick={() => handleOrdenar(campo)}
      className="cursor-pointer inline-flex items-center ml-1 select-none"
    >
      {ordenacao.campo !== campo && (
        <ArrowUpDown size={12} className="opacity-40" />
      )}
      {ordenacao.campo === campo && ordenacao.direcao === 'asc' && (
        <ArrowDown size={12} />
      )}
      {ordenacao.campo === campo && ordenacao.direcao === 'desc' && (
        <ArrowUp size={12} style={{ transform: 'rotate(0deg)' }} />
      )}
    </span>
  );
};

// ======================= COMPONENTE PRINCIPAL =======================
export default function SalariosPorProjeto() {
  const [dados, setDados] = useState([]);
  const [dadosFolha, setDadosFolha] = useState([]);
  const [expandidos, setExpandidos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    meses: [],
    projetos: [],
    funcionarios: [],
  });

  const [ordenacao, setOrdenacao] = useState({
    campo: null,
    direcao: 'asc'
  });

    const handleLogout = () => {
      logout();
      navigate("/");
    };
  const handleOrdenar = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc'
    }));
  };

  // ======================= FUNÇÕES DE API =======================

  const fazerLoginChronos = async () => {
    try {
      const data = await chronosLogin(); // sem argumentos — credenciais ficam no backend
      if (data.success) return true;
      throw new Error(data.error || 'Erro ao fazer login');
    } catch (err) {
      console.error('Erro no login:', err);
      setError('Erro ao fazer login no Chronos: ' + err.message);
      return false;
    }
  };

  const buscarDadosChronos = async (mes, ano) => {
    const inicioMes = `${ano}-${mes.toString().padStart(2, '0')}-01`;
    const ultimoDia = new Date(ano, mes, 0).getDate();
    const finalMes  = `${ano}-${mes.toString().padStart(2, '0')}-${ultimoDia}`;

    const data = await getPontosChronos(inicioMes, finalMes, '0'); // ✅ token enviado automaticamente
    return data?.data ?? [];
  };

  const buscarDadosFolha = async () => {
    const data = await getDadosFolha(); // ✅ token enviado automaticamente
    return data?.data ?? [];
  };
  // Converter horas do formato "HH:MM:SS" para número
  const converterHoras = (horasStr) => {
    if (!horasStr || typeof horasStr !== 'string') return 0;
    const partes = horasStr.split(':');
    const horas = parseInt(partes[0]) || 0;
    const minutos = parseInt(partes[1]) || 0;
    return horas + (minutos / 60);
  };

  // Normalizar nome para comparação
  const normalizarNome = (nome) => {
    if (!nome) return '';
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  // Buscar salário do funcionário na folha
  const buscarSalario = (nomeFuncionario) => {
    const nomeNormalizado = normalizarNome(nomeFuncionario);
    
    const funcionario = dadosFolha.find(f => {
      const nomeColaborador = normalizarNome(f.Colaborador);
      return nomeColaborador.includes(nomeNormalizado) || 
             nomeNormalizado.includes(nomeColaborador);
    });

    if (funcionario && funcionario['VALOR PARA PAGAMENTO AUTOMACAO']) {
      const valor = parseFloat(funcionario['VALOR PARA PAGAMENTO AUTOMACAO']);
      return isNaN(valor) ? 0 : Math.abs(valor);
    }
    
    return 0;
  };

  // ======================= CARREGAR DADOS =======================
  const navigate = useNavigate();
  
  useEffect(() => {
    const carregarDados = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. Fazer login no Chronos
        const loginSucesso = await fazerLoginChronos();
        if (!loginSucesso) {
          throw new Error('Não foi possível fazer login no Chronos');
        }

        // 2. Buscar dados do Chronos
        const hoje = new Date();
        const mes = hoje.getMonth() + 1;
        const ano = hoje.getFullYear();
        const dadosChronos = await buscarDadosChronos(mes, ano);
        setDados(dadosChronos);

        // 3. Buscar dados da folha
        const folha = await buscarDadosFolha();
        setDadosFolha(folha);
        
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setError('Erro ao carregar dados: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, []);

  // ======================= PROCESSAR DADOS COM RATEIO =======================
  const dadosProcessados = useMemo(() => {
    if (!dados.length) return [];

    // 1. Calcular total de horas por funcionário
    const horasPorFuncionario = {};
    dados.forEach(item => {
      const funcionario = item.usuario;
      const horas = converterHoras(item.horas);
      
      if (!horasPorFuncionario[funcionario]) {
        horasPorFuncionario[funcionario] = 0;
      }
      horasPorFuncionario[funcionario] += horas;
    });

    // 2. Agrupar dados por projeto e fazer rateio
    const projetoMap = {};

    dados.forEach(item => {
      const projeto = item.projeto;
      const funcionario = item.usuario;
      const horas = converterHoras(item.horas);
      const mesRef = item.mes_referencia;

      // Buscar salário total do funcionário
      const salarioTotal = buscarSalario(funcionario);
      const horasTotaisFuncionario = horasPorFuncionario[funcionario] || 1;
      
      // Calcular salário rateado proporcionalmente às horas
      const salarioRateado = (horas / horasTotaisFuncionario) * salarioTotal;

      if (!projetoMap[projeto]) {
        projetoMap[projeto] = {
          projeto,
          funcionarios: []
        };
      }

      projetoMap[projeto].funcionarios.push({
        nome: funcionario,
        mes: mesRef,
        horas: Math.round(horas * 10) / 10, // 1 casa decimal
        horasTotais: Math.round(horasTotaisFuncionario * 10) / 10,
        salarioTotal: salarioTotal,
        salarioRateado: salarioRateado
      });
    });

    return Object.values(projetoMap);
  }, [dados, dadosFolha]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  // ======================= OPTIONS DINÂMICAS =======================
  const mesesOptions = useMemo(() => {
    const meses = [...new Set(dadosProcessados.flatMap(p => p.funcionarios.map(f => f.mes)))];
    return meses.sort().map(m => ({ label: m, value: m }));
  }, [dadosProcessados]);

  const projetosOptions = useMemo(() => {
    return [...new Set(dadosProcessados.map(p => p.projeto))]
      .sort()
      .map(proj => ({ value: proj, label: proj }));
  }, [dadosProcessados]);

  const funcionariosOptions = useMemo(() => {
    const nomes = [...new Set(dadosProcessados.flatMap(p => p.funcionarios.map(f => f.nome)))];
    return nomes.sort().map(nome => ({ value: nome, label: nome }));
  }, [dadosProcessados]);

  // ======================= FILTRAGEM =======================
  const dadosFiltrados = useMemo(() => {
    return dadosProcessados
      .map((p) => ({
        ...p,
        funcionarios: p.funcionarios.filter((f) => {
          const filtroMes =
            filters.meses.length === 0 || filters.meses.includes(f.mes);
          const filtroProjeto =
            filters.projetos.length === 0 || filters.projetos.includes(p.projeto);
          const filtroFunc =
            filters.funcionarios.length === 0 ||
            filters.funcionarios.includes(f.nome);

          return filtroMes && filtroProjeto && filtroFunc;
        }),
      }))
      .filter((p) => p.funcionarios.length > 0);
  }, [dadosProcessados, filters]);

  // ======================= ORDENAÇÃO =======================
  const dadosOrdenados = useMemo(() => {
    if (!ordenacao.campo) return dadosFiltrados;

    const sorted = [...dadosFiltrados];

    sorted.sort((a, b) => {
      let va, vb;

      switch (ordenacao.campo) {
        case 'projeto':
          va = a.projeto.toLowerCase();
          vb = b.projeto.toLowerCase();
          break;
        case 'funcionarios':
          va = a.funcionarios.length;
          vb = b.funcionarios.length;
          break;
        case 'horas':
          va = a.funcionarios.reduce((s, f) => s + f.horas, 0);
          vb = b.funcionarios.reduce((s, f) => s + f.horas, 0);
          break;
        case 'salarios':
          va = a.funcionarios.reduce((s, f) => s + f.salarioRateado, 0);
          vb = b.funcionarios.reduce((s, f) => s + f.salarioRateado, 0);
          break;
        default:
          return 0;
      }

      return ordenacao.direcao === 'asc'
        ? va > vb ? 1 : -1
        : va < vb ? 1 : -1;
    });

    return sorted;
  }, [dadosFiltrados, ordenacao]);

  const toggleProjeto = (projeto) =>
    setExpandidos(prev => ({ ...prev, [projeto]: !prev[projeto] }));

  const limparFiltros = () => {
    setFilters({
      meses: [],
      funcionarios: [],
      projetos: []
    });
  };

  const totalSalarios = dadosFiltrados.reduce(
    (acc, p) => acc + p.funcionarios.reduce((s, f) => s + f.salarioRateado, 0),
    0
  );

  // ======================= RENDER =======================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin mx-auto mb-4" size={48} />
          <p className="text-gray-600">Carregando dados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
          <h2 className="text-xl font-bold text-center mb-2">Erro ao carregar dados</h2>
          <p className="text-gray-600 text-center">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 w-full px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-pink-800 text-white px-6 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <span className="text-2xl font-bold">YOUX</span>
          <span className="text-xl font-light">GROUP</span>
        </div>
        <div className="flex items-center space-x-3"> 
          <button
            onClick={() => {
              navigate('/inicio');
            }}
            className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-lg text-sm font-bold"
          >
            Menu Principal
          </button>   
        </div>
      </header>

      <div className="p-6">
        {/* Cabeçalho */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">
            Detalhamento de Custo com Rateio
          </h1>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total de Salários Rateados</p>
            <p className="text-2xl font-bold text-pink-600">{formatCurrency(totalSalarios)}</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs mb-1 font-medium">Projeto</label>
            <MultiSelect
              options={projetosOptions}
              value={filters.projetos}
              onChange={(v) => setFilters(f => ({ ...f, projetos: v }))}
              placeholder="Todos os projetos"
            />
          </div>

          <div>
            <label className="block text-xs mb-1 font-medium">Funcionário</label>
            <MultiSelect
              options={funcionariosOptions}
              value={filters.funcionarios}
              onChange={(v) => setFilters(f => ({ ...f, funcionarios: v }))}
              placeholder="Todos os funcionários"
            />
          </div>

          <div>
            <label className="block text-xs mb-1 font-medium">Mês</label>
            <MultiSelect
              options={mesesOptions}
              value={filters.meses}
              onChange={(v) => setFilters(f => ({ ...f, meses: v }))}
              placeholder="Todos os meses"
            />
          </div>

          <div className="flex items-end">
            <button 
              onClick={limparFiltros} 
              className="px-4 py-2 text-pink-600 hover:bg-pink-50 rounded-lg transition-colors w-full"
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-2">
                  Projeto
                  <IconeOrdenacao campo="projeto" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                </th>
                <th className="text-right py-3 px-2">
                  Funcionários
                  <IconeOrdenacao campo="funcionarios" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                </th>
                <th className="text-right py-3 px-2">
                  Horas
                  <IconeOrdenacao campo="horas" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                </th>
                <th className="text-right py-3 px-2">
                  Salários Rateados
                  <IconeOrdenacao campo="salarios" ordenacao={ordenacao} handleOrdenar={handleOrdenar} />
                </th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {dadosOrdenados.map((proj) => {
                const horasTotais = proj.funcionarios.reduce((acc, f) => acc + f.horas, 0);
                const salariosTotais = proj.funcionarios.reduce((acc, f) => acc + f.salarioRateado, 0);

                const funcionariosOrdenados = [...proj.funcionarios].sort((a, b) => a.nome.localeCompare(b.nome));

                return (
                  <React.Fragment key={proj.projeto}>
                    <tr className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2 font-semibold">{proj.projeto}</td>
                      <td className="text-right py-3 px-2">{proj.funcionarios.length}</td>
                      <td className="text-right py-3 px-2">{Math.round(horasTotais * 10) / 10}h</td>
                      <td className="text-right py-3 px-2">{formatCurrency(salariosTotais)}</td>
                      <td className="py-3 px-2 text-center">
                        <button onClick={() => toggleProjeto(proj.projeto)} className="text-indigo-600">
                          {expandidos[proj.projeto] ? '▼' : '▶'}
                        </button>
                      </td>
                    </tr>

                    {expandidos[proj.projeto] && (
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-6 py-4">
                          <table className="w-full text-xs border">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="text-left px-2 py-1">Funcionário</th>
                                <th className="text-right px-2 py-1">Mês</th>
                                <th className="text-right px-2 py-1">Horas Projeto</th>
                                <th className="text-right px-2 py-1">Horas Totais</th>
                                <th className="text-right px-2 py-1">Salário Total</th>
                                <th className="text-right px-2 py-1">Salário Rateado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {funcionariosOrdenados.map((f, i) => (
                                <tr key={i} className="border-t">
                                  <td className="px-2 py-1">{f.nome}</td>
                                  <td className="text-right px-2 py-1">{f.mes}</td>
                                  <td className="text-right px-2 py-1">{f.horas}h</td>
                                  <td className="text-right px-2 py-1">{f.horasTotais}h</td>
                                  <td className="text-right px-2 py-1">{formatCurrency(f.salarioTotal)}</td>
                                  <td className="text-right px-2 py-1 font-semibold text-pink-600">
                                    {formatCurrency(f.salarioRateado)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {dadosOrdenados.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Nenhum dado encontrado com os filtros aplicados
            </div>
          )}
        </div>
      </div>
    </div>
  );
}